import React, { useCallback, useEffect, useRef, useState } from 'react';

import { UITreeBranch } from './ui-tree-branch/ui-tree-branch';
import { UITreeNode } from './types';
import { generateIndexFromTreeNodes } from './utils';

export interface UITreeProps<T = unknown> {
  className?: string;
  component: React.FunctionComponent<UITreeNode<T>>;
  style?: React.CSSProperties;
  tree?: UITreeNode<T>[];
  searchCriteria?: string; // used to achieve auto expand / collapse on search
}

export const UITree = <T,>(props: UITreeProps<T>) => {
  const { component, searchCriteria, tree = [], ...otherProps } = props;
  const [expandedBranches, setExpandedBranches] = useState<UITreeNode['id'][]>([]);
  const index = useRef(generateIndexFromTreeNodes(tree));

  const handleToggleGroup = useCallback(
    (id: UITreeNode['id']) => {
      if (expandedBranches.includes(id)) {
        setExpandedBranches(expandedBranches.filter(expVal => expVal !== id));
      } else {
        setExpandedBranches([...expandedBranches, id]);
      }
    },
    [expandedBranches]
  );

  // When tree changes, rebuild the index
  useEffect(() => {
    index.current = generateIndexFromTreeNodes(tree);
  }, [tree]);

  // When starting (no search criteria) all groups are closed
  // When doing a search, all groups are opened on every key press
  // When clearing search criteria, all groups are closed again
  useEffect(() => {
    if (searchCriteria) {
      setExpandedBranches(
        Object.values(index.current)
          .filter(node => node.type === 'group')
          .map(node => node.id)
      );
    } else {
      setExpandedBranches([]);
    }
  }, [searchCriteria]);

  return (
    <div data-testid="UITree" {...otherProps}>
      <div>
        {tree.map(option => (
          <UITreeBranch
            component={component}
            key={option.id}
            {...option}
            expandedBranches={expandedBranches}
            onToggleGroup={handleToggleGroup}
          />
        ))}
      </div>
    </div>
  );
};
